import {
    DeleteOutlined,
    DownloadOutlined,
    SyncOutlined,
} from "@ant-design/icons";
import {
    Button,
    Col,
    Collapse,
    CollapseProps,
    Empty,
    Flex,
    Popconfirm,
    Row,
} from "antd";
import dayjs from "dayjs";
import { MouseEvent, useCallback, useState } from "react";
import { displayDateFormat } from "tm-shared/constants";
import { createUniqueId } from "tm-shared/utils";

import { PrimaryText } from "@/components/Typography/Styled";
import { TaskProgressDisplay } from "@/components/UI/TaskProgressDisplay";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { QueueTaskSnapshot, QueueTaskStatus } from "@/hooks/useAsyncTaskQueue";
import { useMultiTap } from "@/hooks/useMultiTap";
import { useQueueInfo } from "@/hooks/useQueueInfo";
import { useUserPayload } from "@/hooks/useUserPayload";
import {
    deleteRequestQueueItem,
    restartRequestQueue,
} from "@/store/slices/offline";
import { RequestQueueItem } from "@/types/requestQueue";
import { copyObjectToClipboard, mapRequestQueueItemForCopy } from "@/utils";

import { QueueItemError } from "./components/QueueItemError";
import { QueueItemMetadata } from "./components/QueueItemMetadata";
import { QueueItemProgress } from "./components/QueueItemProgress";
import { StyledButton } from "./Styled";

type QueueVariant = "request" | "failed";

const getProgressDisplayData = (
    item: RequestQueueItem,
): QueueTaskSnapshot[] => {
    if (item.progress) {
        return item.progress.tasks;
    }

    return item.commands.map(({ title }) => ({
        id: createUniqueId(),
        label: title,
        status: QueueTaskStatus.Pending,
    }));
};

export const SyncRequests = () => {
    const { offlineMode, requestQueue } = useAppSelector(
        (state) => state.offline,
    );
    const { failedQueue } = useAppSelector((state) => state.ui);
    const dispatch = useAppDispatch();

    const { userPayload } = useUserPayload();
    const { isRunning: isQueueRunning } = useQueueInfo();

    const [deleteMode, setDeleteMode] = useState(false);

    const enableEditMode = useMultiTap(
        () => {
            setDeleteMode(true);
        },
        { taps: 3 },
    );

    const handleDeleteQueueItem = useCallback(
        (itemId: string) => {
            if (!deleteMode) return;

            dispatch(deleteRequestQueueItem(itemId));
        },
        [deleteMode],
    );

    const tasksLength = requestQueue.length;

    const handleDownloadQueue = useCallback(() => {
        const cleanQueue = requestQueue.map(mapRequestQueueItemForCopy);
        const result = { user: userPayload, queue: cleanQueue };
        const json = JSON.stringify(result, null, 2);
        const blob = new Blob([json], {
            type: "application/json;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);

        const safeIso = new Date().toISOString().replace(/[:.]/g, "-");
        const filename = `queue-${userPayload.userId}-${safeIso}.json`;

        const a = document.createElement("a");
        Object.assign(a, {
            href: url,
            download: filename,
            style: { display: "none" },
            rel: "noopener",
        });

        document.body.appendChild(a);
        a.click();
        a.remove();

        setTimeout(() => URL.revokeObjectURL(url), 0);
    }, [requestQueue, userPayload]);

    const handleRetryAll = useCallback(() => {
        dispatch(restartRequestQueue());
    }, [dispatch]);

    const handleRetryTask = useCallback(
        (id: string) => {
            dispatch(restartRequestQueue(id));
        },
        [dispatch],
    );

    const handleCopyErrorItem = useCallback(
        (e: MouseEvent<HTMLElement>, item: RequestQueueItem) => {
            e.preventDefault();
            e.stopPropagation();

            const failedIndex = item.progress?.lastErrorIndex;
            if (failedIndex == null) return;

            const error = item.progress?.tasks?.[failedIndex]?.error;
            const failedTask = item.commands?.[failedIndex];
            if (!failedTask) return;

            copyObjectToClipboard({ user: userPayload, failedTask, error });
        },
        [userPayload],
    );

    const buildCollapseItems = useCallback(
        (
            list: RequestQueueItem[],
            variant: QueueVariant,
        ): CollapseProps["items"] =>
            list.map((item) => {
                const { id, progress } = item;
                const isFailed = variant === "failed";

                const error =
                    progress?.tasks?.[progress?.lastErrorIndex ?? ""]?.error;
                const progressDisplayData = getProgressDisplayData(item);

                const showSyncButton =
                    !isFailed && error && !isQueueRunning && !offlineMode;

                const createdAtDayjs = dayjs(item.createdAt);
                const showCreatedDate = !dayjs().isSame(createdAtDayjs, "day");

                return {
                    key: `${variant}:${id}`,
                    showArrow: false,
                    label: (
                        <Flex vertical gap={4}>
                            {!isFailed && (
                                <QueueItemProgress progress={progress} />
                            )}

                            {item?.metadata && (
                                <QueueItemMetadata metadata={item.metadata} />
                            )}

                            {showCreatedDate && (
                                <Flex gap={8}>
                                    <PrimaryText>Дата створення:</PrimaryText>
                                    <PrimaryText
                                        $textColor="warning"
                                        fontWeight="500"
                                    >
                                        {createdAtDayjs.format(
                                            displayDateFormat.dateTime,
                                        )}
                                    </PrimaryText>
                                </Flex>
                            )}

                            <QueueItemError
                                error={error}
                                onCopy={(e) => handleCopyErrorItem(e, item)}
                            />

                            {deleteMode && (
                                <div
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ width: "fit-content" }}
                                >
                                    <Popconfirm
                                        title="Ви впевнені? Дія є незворотною"
                                        onConfirm={() =>
                                            handleDeleteQueueItem(id)
                                        }
                                        okText="Так"
                                        cancelText="Ні"
                                    >
                                        <StyledButton
                                            type="primary"
                                            icon={<DeleteOutlined />}
                                            danger
                                        >
                                            <PrimaryText $textColor="white">
                                                Видалити
                                            </PrimaryText>
                                        </StyledButton>
                                    </Popconfirm>
                                </div>
                            )}
                        </Flex>
                    ),
                    children: (
                        <Flex vertical gap={2}>
                            <TaskProgressDisplay tasks={progressDisplayData} />

                            {showSyncButton && (
                                <Button
                                    type="link"
                                    icon={<SyncOutlined />}
                                    onClick={() => handleRetryTask(id)}
                                >
                                    Повторити
                                </Button>
                            )}
                        </Flex>
                    ),
                };
            }),
        [isQueueRunning, handleCopyErrorItem, handleRetryTask, deleteMode],
    );

    if (tasksLength === 0 && failedQueue.length === 0) {
        return (
            <Empty description="Черга порожня — немає запитів для синхронізації" />
        );
    }

    const requestItems = buildCollapseItems(requestQueue, "request");
    const failedItems = buildCollapseItems(failedQueue, "failed");

    return (
        <Flex vertical gap={12}>
            {tasksLength > 0 && (
                <>
                    <Flex vertical gap={12}>
                        <Button
                            type="text"
                            style={{
                                width: "fit-content",
                                height: "fit-content",
                                padding: 0,
                            }}
                            onClick={enableEditMode}
                        >
                            <PrimaryText fontSize={16}>
                                Задач у черзі: {tasksLength}
                            </PrimaryText>
                        </Button>

                        <Row gutter={8}>
                            <Col span={12}>
                                <StyledButton
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    onClick={handleDownloadQueue}
                                    disabled={isQueueRunning}
                                >
                                    Завантажити
                                </StyledButton>
                            </Col>

                            <Col span={12}>
                                <StyledButton
                                    type="primary"
                                    icon={<SyncOutlined />}
                                    disabled={offlineMode || isQueueRunning}
                                    onClick={handleRetryAll}
                                >
                                    Синхронізувати
                                </StyledButton>
                            </Col>
                        </Row>
                    </Flex>

                    <Collapse items={requestItems} />
                </>
            )}

            {failedQueue.length > 0 && !isQueueRunning && (
                <Flex vertical gap={8}>
                    <PrimaryText fontSize={16}>
                        Помилки без можливості повтору: {failedQueue.length}
                    </PrimaryText>

                    <Collapse items={failedItems} />
                </Flex>
            )}
        </Flex>
    );
};
